import {
  SwapState,
  getMintableAssets,
  getMintableCollections,
  getMintableVTokens,
  getRedeemableAssets,
  getRedeemableCollections,
  getRedeemableVTokens,
} from '@/domain/swap';
import {
  Address,
  Asset,
  MarketplacePrice,
  Token,
  Vault,
  WeiPerEther,
  Zero,
  addressEqual,
  getTotalTokenIds,
} from 'nftx.js';
import { useMemo } from 'react';
import useUserAssets from '@/application/read/assets/useUserAssets';
import useVaults from '@/application/read/vaults/useVaults';
import useBalances from '@/application/read/tokens/useBalances';
import useBuyAmount from '@/application/read/swap/useBuyAmount';
import useSellAmount from '@/application/read/swap/useSellAmount';
import { formatEther, parseEther } from 'viem';
import { QueryStatus } from '@tanstack/react-query';
import { useBalance } from 'wagmi';
import { useFormatter } from 'use-intl';
import useVaultAssets from '../assets/useVaultAssets';
import { SwapPrice } from '@/core/swap';
import { getLoadingState } from '@/infrastructure/utils/getLoadingState';

export type UseSwapDataReturn = {
  /** The currently-selected sell vault (if applicable) */
  sellVault: Vault | undefined;
  /** Either the given sellAmount or the calculated sellAmount, depending on amountController */
  sellAmount: `${number}`;
  /** The sell amount in ETH terms (based on spot price) */
  sellEthValue: bigint | undefined;
  /** The symbol/name of the token being sold */
  sellSymbol: string;
  /** A list of collections that the user has eligible NFTs for.
   * If filterController is "buy" then this list will be filtered */
  mintableCollections: Array<
    Token & { vaultIds: string[]; totalHoldings: number }
  >;
  /** A list of the user's eligible NFTs.
   * If filterController is "buy" then this list will be filtered.
   * Will be filtered by mintableCollections.
   * If sellToken is provided, this will be filtered by collection.
   * If sellVaultId is provided, it will be further filtered by vault */
  mintableAssets: Asset[];
  /** A list of the user's sellable vTokens.
   * If filterController is "buy" then this list will be filtered.
   */
  mintableVTokens: Array<Token & { vaultId: string; balance: bigint }>;
  /** The amount the user has for the currently-selected token */
  sellBalance: `${number}`;

  /** A list of currently-selected buy vaults */
  buyVault: Vault | undefined;
  /** Either the given buyAmount of the calculated buy amount, depending on amountController */
  buyAmount: `${number}`;
  /** The amount the user has for the currently-selected token */
  buyBalance: `${number}`;
  /** The buy amount in ETH terms (based on sell price) */
  buyEthValue: bigint | undefined;
  /** The symbol/name of the token being bought */
  buySymbol: string;
  /** A list of collections for all vaults.
   * If filterController is "sell" then this list will be filtered.
   */
  redeemableCollections: Array<
    Token & { vaultIds: string[]; totalHoldings: number }
  >;
  /** A list of NFTs available to buy from vaults.
   * Only populated if buyType is NFT and buyToken is set
   * Will be filtered by redeemableCollections
   */
  redeemableAssets: Asset[];
  /** A list of all vTokens you can buy (i.e. all vaults).
   * If filterController is "sell" then this list will be filtered.
   */
  redeemableVTokens: Array<Token & { vaultId: string; balance: bigint }>;

  source: string;
  /** A string representation of the route */
  route: undefined | MarketplacePrice['route'];
  estimatedGasFee: bigint | undefined;

  /** The quote to be used to make the transaction */
  quote?: SwapPrice | null;

  // Statuses
  mintableAssetsStatus: QueryStatus;
  vaultsStatus: QueryStatus;
  balancesStatus: QueryStatus;
  redeemableAssetsStatus: QueryStatus;
  buyAmountLoading: boolean;
  sellAmountLoading: boolean;

  error: any;
};

// TODO: refactor and split this out
const useSwapData = ({
  userAddress,
  amountController,
  buyAmount: buyAmountInput,
  buyItems,
  buyToken,
  buyType,
  buyVaultId,
  filterController,
  sellAmount: sellAmountInput,
  sellToken,
  sellItems,
  sellType,
  sellVaultId,
}: SwapState & { userAddress: Address | undefined }): UseSwapDataReturn => {
  const sellTotal = getTotalTokenIds(sellItems);
  const buyTotal = getTotalTokenIds(buyItems);
  const formatter = useFormatter();
  const { data: ethBalance } = useBalance({ address: userAddress });
  const {
    data: allMintableAssets,
    status: mintableAssetsStatus,
    error: mintableAssetsError,
  } = useUserAssets(
    { vaultId: sellVaultId, userAddress, mintable: true },
    { enabled: !!userAddress }
  );
  const {
    data: allVaults,
    status: vaultsStatus,
    error: vaultsError,
  } = useVaults();
  const {
    data: balances,
    status: balancesStatus,
    error: balancesError,
  } = useBalances({ userAddress });
  const {
    data: allRedeemableAssets,
    status: redeemableAssetsStatus,
    error: redeemableAssetsError,
  } = useVaultAssets(
    { vaultId: buyVaultId },
    { enabled: !!(buyType === 'NFT' && buyToken) }
  );
  const {
    data: buyPrice,
    isLoading: buyAmountQueryLoading,
    fetchStatus: buyAmountFetchStatus,
    error: buyAmountError,
  } = useBuyAmount(
    {
      buyToken: buyToken as Address,
      buyType,
      sellAmount: sellAmountInput as `${number}`,
      sellToken: sellToken as Address,
      sellTokenIds: sellItems,
      sellType,
      sellVaultId,
      userAddress,
      buyTokenIds: buyItems,
    },
    { enabled: amountController === 'sell' }
  );
  const buyAmountLoading = getLoadingState(
    buyAmountQueryLoading,
    buyAmountFetchStatus
  );
  const {
    data: sellPrice,
    isLoading: sellAmountQueryLoading,
    fetchStatus: sellAmountFetchStatus,
    error: sellAmountError,
  } = useSellAmount(
    {
      buyAmount: buyAmountInput as `${number}`,
      buyToken: buyToken as Address,
      buyTokenIds: buyItems,
      buyType,
      sellToken: sellToken as Address,
      sellType,
      buyVaultId,
      sellTokenIds: sellItems,
      userAddress,
    },
    { enabled: amountController === 'buy' }
  );
  const sellAmountLoading = getLoadingState(
    sellAmountQueryLoading,
    sellAmountFetchStatus
  );

  const quote = amountController === 'buy' ? sellPrice : buyPrice;
  const quoteError =
    amountController === 'buy' ? sellAmountError : buyAmountError;

  const error =
    vaultsError ||
    mintableAssetsError ||
    balancesError ||
    redeemableAssetsError ||
    quoteError;

  const mintableAssets = useMemo(() => {
    return getMintableAssets({
      buyToken,
      buyType,
      filterController,
      mintableAssets: allMintableAssets,
      sellType,
      sellToken,
      sellVaultId,
      vaults: allVaults,
      buyVaultId,
    });
  }, [
    allMintableAssets,
    allVaults,
    buyToken,
    buyType,
    buyVaultId,
    filterController,
    sellToken,
    sellType,
    sellVaultId,
  ]);

  const mintableCollections = useMemo(() => {
    return getMintableCollections({ mintableAssets, vaults: allVaults });
  }, [allVaults, mintableAssets]);

  const mintableVTokens = useMemo(() => {
    return getMintableVTokens({
      balances,
      buyToken,
      buyType,
      filterController,
      vaults: allVaults,
      buyVaultId,
    });
  }, [allVaults, balances, buyToken, buyType, buyVaultId, filterController]);

  const redeemableCollections = useMemo(() => {
    return getRedeemableCollections({
      buyItems,
      filterController,
      sellToken,
      sellType,
      sellVaultId,
      vaults: allVaults,
    });
  }, [allVaults, buyItems, filterController, sellToken, sellType, sellVaultId]);

  const redeemableAssets = useMemo(() => {
    return getRedeemableAssets({ allRedeemableAssets, redeemableCollections });
  }, [allRedeemableAssets, redeemableCollections]);

  const redeemableVTokens = useMemo(() => {
    return getRedeemableVTokens({
      filterController,
      sellToken,
      sellType,
      sellVaultId,
      vaults: allVaults,
      balances,
    });
  }, [allVaults, filterController, sellToken, sellType, sellVaultId, balances]);

  const sellVault = useMemo(() => {
    if (!allVaults) {
      return;
    }
    if (sellType === 'NFT' && sellVaultId) {
      return allVaults.find((vault) => vault.vaultId === sellVaultId);
    }
    if (sellType === 'vTOKEN' && sellToken) {
      return allVaults.find((vault) => addressEqual(vault.id, sellToken));
    }
  }, [allVaults, sellToken, sellType, sellVaultId]);

  const buyVault = useMemo(() => {
    return allVaults?.find((vault) => vault.vaultId === buyVaultId);
  }, [allVaults, buyVaultId]);
  const buyAmount = useMemo(() => {
    if (amountController === 'buy') {
      return buyAmountInput as `${number}`;
    }
    return formatter.number(Number(formatEther(buyPrice?.amount ?? Zero)), {
      maximumFractionDigits: 4,
      useGrouping: false,
    }) as `${number}`;
  }, [amountController, buyAmountInput, buyPrice?.amount, formatter]);
  const sellAmount = useMemo(() => {
    if (amountController === 'sell') {
      return sellAmountInput as `${number}`;
    }
    return formatter.number(Number(formatEther(sellPrice?.amount ?? Zero)), {
      useGrouping: false,
      maximumFractionDigits: 4,
    }) as `${number}`;
  }, [amountController, formatter, sellAmountInput, sellPrice?.amount]);

  const sellBalance = useMemo((): `${number}` => {
    if (sellType === 'ETH' && ethBalance) {
      return ethBalance.formatted as `${number}`;
    }
    if (sellType === 'vTOKEN' && sellToken && balances) {
      const balance = balances.find((x) =>
        addressEqual(x.id, sellToken)
      )?.balance;
      if (balance) {
        return formatEther(balance) as `${number}`;
      }
    }
    return '0';
  }, [balances, ethBalance, sellToken, sellType]);
  const buyBalance = useMemo(() => {
    if (buyType === 'ETH' && ethBalance) {
      return ethBalance.formatted as `${number}`;
    }
    if (buyType === 'vTOKEN' && buyToken && balances) {
      const balance = balances.find((x) =>
        addressEqual(x.id, buyToken)
      )?.balance;
      if (balance) {
        return formatEther(balance) as `${number}`;
      }
    }
    return '0';
  }, [balances, buyToken, buyType, ethBalance]);

  const sellEthValue = useMemo(() => {
    const spot = sellVault?.prices?.[0]?.mint?.price;
    if (!spot) {
      return;
    }
    if (sellType === 'NFT') {
      return spot * BigInt(sellTotal);
    }
    return (spot * parseEther(sellAmount)) / WeiPerEther;
  }, [sellAmount, sellTotal, sellType, sellVault?.prices]);

  const buyEthValue = useMemo(() => {
    const spot = buyVault?.prices?.[0]?.redeem?.price;
    if (!spot) {
      return;
    }
    if (buyType === 'NFT') {
      return spot * BigInt(buyTotal);
    }
    return (spot * parseEther(buyAmount)) / WeiPerEther;
  }, [buyAmount, buyTotal, buyType, buyVault?.prices]);

  const buySymbol = useMemo(() => {
    if (buyType === 'ETH') {
      return 'ETH';
    }
    if (buyType === 'NFT' && buyVault) {
      return buyVault.token.name;
    }
    if (buyType === 'vTOKEN' && buyVault) {
      return buyVault.token.symbol;
    }
    return '';
  }, [buyType, buyVault]);

  const sellSymbol = useMemo(() => {
    if (sellType === 'ETH') {
      return 'ETH';
    }
    if (sellType === 'NFT' && sellVault) {
      return sellVault.token.name;
    }
    if (sellType === 'vTOKEN' && sellVault) {
      return sellVault.token.symbol;
    }
    return '';
  }, [sellType, sellVault]);

  const source = '';
  const route = quote?.route;
  const estimatedGasFee = useMemo(() => {
    // TODO: estimate gas
    // if (amountController === 'buy') {
    //   return sellPrice?.estimatedGas;
    // }
    // return buyPrice?.estimatedGas;
    return undefined;
  }, []);

  return {
    buyAmount,
    buyEthValue,
    mintableAssets,
    mintableCollections,
    mintableVTokens,
    redeemableAssets,
    redeemableCollections,
    redeemableVTokens,
    sellAmount,
    sellEthValue,
    sellBalance,
    buyBalance,
    sellVault,
    buyVault,
    buySymbol,
    estimatedGasFee,
    source,
    route,
    sellSymbol,
    quote,

    balancesStatus,
    buyAmountLoading,
    mintableAssetsStatus,
    redeemableAssetsStatus,
    sellAmountLoading,
    vaultsStatus,

    error,
  };
};

export default useSwapData;
